import { useCallback, useState } from "react";
import { LockKey, PaperPlaneTilt, ShieldWarning } from "@phosphor-icons/react";
import { api } from "@/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/components/ui/sonner";
import { censorMessage, decryptForCurrentUser, encryptForUser, ensureMessagingKeys } from "@/cryptoMessaging";

export default function MessagesPage({ user, authLoading, onNotificationsChanged }) {
  const urlParams = new URLSearchParams(typeof window !== "undefined" ? window.location.search : "");
  const querySellerId = urlParams.get("seller_id");
  const queryListingId = urlParams.get("listing_id");
  const [conversations, setConversations] = useState([]);
  const [activeId, setActiveId] = useState("");
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [privateJwk, setPrivateJwk] = useState(null);
  const [decrypted, setDecrypted] = useState({});
  const [inboxLoading, setInboxLoading] = useState(false);

  const registerKeys = useCallback(async () => {
    if (!user) return;
    const keys = await ensureMessagingKeys(user.id);
    setPrivateJwk(keys.privateJwk);
    await api.post("/crypto/public-key", { public_key: JSON.stringify(keys.publicJwk) });
  }, [user]);

  const loadMessages = useCallback(async (conversationId, keyOverride = null) => {
    if (!conversationId) return;
    const { data } = await api.get(`/conversations/${conversationId}/messages`);
    setMessages(data);
    const keyToUse = keyOverride || privateJwk;
    if (keyToUse && user) {
      const next = {};
      for (const message of data) {
        const mine = message.ciphertexts.find((item) => item.recipient_id === user.id);
        next[message.id] = mine ? await decryptForCurrentUser(keyToUse, mine.ciphertext) : "[Encrypted for another recipient]";
      }
      setDecrypted(next);
    }
  }, [privateJwk, user]);

  const loadConversations = useCallback(async () => {
    const { data } = await api.get("/conversations");
    setConversations(data);
    if (!activeId && data.length) {
      setActiveId(data[0].id);
      await loadMessages(data[0].id);
    }
  }, [activeId, loadMessages]);

  const loadInbox = async () => {
    if (!user) return;
    setInboxLoading(true);
    try {
      const keys = await ensureMessagingKeys(user.id);
      setPrivateJwk(keys.privateJwk);
      await api.post("/crypto/public-key", { public_key: JSON.stringify(keys.publicJwk) });
      const { data } = await api.get("/conversations");
      setConversations(data);
      if (data.length) {
        const selectedId = activeId || data[0].id;
        setActiveId(selectedId);
        await loadMessages(selectedId, keys.privateJwk);
      }
      toast.success(data.length ? "Encrypted inbox loaded" : "No conversations yet");
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || "Could not load encrypted inbox");
    } finally {
      setInboxLoading(false);
    }
  };

  const sendMessage = (event) => {
    event.preventDefault();
    const active = conversations.find((conversation) => conversation.id === activeId);
    if (!active || !draft.trim()) return;
    const { censored, flagged_terms, was_censored } = censorMessage(draft.trim());
    Promise.all(active.participant_ids.map((participantId) => api.get(`/users/${participantId}/public-key`).then(({ data }) => {
      if (!data.public_key) throw new Error("The other user needs to open Messages once before encrypted messaging is available.");
      return encryptForUser(JSON.parse(data.public_key), censored).then((ciphertext) => ({ recipient_id: participantId, ciphertext }));
    }))).then((ciphertexts) => api.post(`/conversations/${activeId}/messages`, { ciphertexts, flagged_terms, was_censored }))
      .then(({ data }) => {
        onNotificationsChanged?.();
        toast.success(was_censored ? "Message sent with prohibited content removed" : "Encrypted message sent");
        window.setTimeout(() => window.location.reload(), 500);
      })
      .catch((error) => toast.error(error.message || error.response?.data?.detail || "Could not send message"));
  };

  if (authLoading) return <main data-testid="messages-loading" className="p-10 text-[#F7F2EA]">Loading messages...</main>;
  if (!user) return <main data-testid="messages-auth-required" className="p-10 text-[#F7F2EA]">Please sign in to message sellers.</main>;
  if (!user.seller_verified) return <main data-testid="messages-verification-required" className="min-h-[calc(100vh-74px)] bg-[#26352B] p-10 text-[#F7F2EA]"><h1 data-testid="messages-verification-heading" className="text-4xl font-light">Stripe Connect required</h1><p data-testid="messages-verification-description" className="mt-4 max-w-xl text-[#E8E4D9]">Complete Stripe Connect onboarding in Seller Studio to unlock verified-member messaging.</p><Button data-testid="messages-verify-link" className="mt-6 rounded-none bg-[#D86F55]" onClick={() => { window.location.href = "/seller"; }}>Go to Seller Studio</Button></main>;
  const active = conversations.find((conversation) => conversation.id === activeId);
  const pendingSellerId = querySellerId;

  return (
    <main data-testid="messages-page" className="min-h-[calc(100vh-74px)] bg-[#26352B] px-4 py-8 text-[#F7F2EA] md:px-8">
      <section className="mx-auto grid max-w-7xl gap-6 lg:grid-cols-[330px_1fr]">
        <aside data-testid="conversation-list" className="border border-[#8E3F31] bg-[#1F2E25] p-4">
          <p data-testid="messages-kicker" className="text-xs uppercase tracking-[0.22em] text-[#D86F55]">Encrypted inbox</p>
          <h1 data-testid="messages-heading" className="mt-2 text-4xl font-light">Messages</h1>
          <Button data-testid="load-inbox-button" className="mt-5 w-full rounded-none bg-[#D86F55]" onClick={loadInbox} disabled={inboxLoading}>{inboxLoading ? "Loading encrypted inbox..." : "Load encrypted inbox"}</Button>
          <div className="mt-5 space-y-2">{conversations.map((conversation) => <button data-testid={`conversation-${conversation.id}`} key={conversation.id} className={`w-full border p-3 text-left text-sm ${activeId === conversation.id ? "border-[#D86F55]" : "border-[#3E5144]"}`} onClick={() => { setActiveId(conversation.id); loadMessages(conversation.id); }}><span className="block font-semibold">{conversation.listing_title || "General conversation"}</span><span className="text-[#E8E4D9]">{Object.values(conversation.participant_names).join(" / ")}</span></button>)}</div>
        </aside>
        <section data-testid="message-thread" className="border border-[#8E3F31] bg-[#1F2E25] p-4">
          <div className="mb-4 flex items-center gap-2 text-sm text-[#E8E4D9]"><LockKey size={18} /> End-to-end encrypted. Prohibited content is censored on your device before encryption.</div>
          {pendingSellerId && !active && <Button data-testid="start-param-conversation-button" className="mb-4 rounded-none bg-[#D86F55]" onClick={() => api.post("/conversations", { seller_id: querySellerId, listing_id: queryListingId }).then(() => { window.location.href = "/messages"; }).catch((error) => toast.error(error.response?.data?.detail || "Could not start conversation"))}>Start encrypted conversation with seller</Button>}
          {!active ? <p data-testid="no-conversation-message">No conversations yet.</p> : <>
            <div data-testid="message-list" className="max-h-[56vh] space-y-3 overflow-y-auto pr-2">{messages.map((message) => <article data-testid={`message-${message.id}`} key={message.id} className={`border p-3 ${message.sender_id === user.id ? "ml-auto max-w-[85%] border-[#D86F55]" : "mr-auto max-w-[85%] border-[#3E5144]"}`}><p className="text-xs uppercase tracking-[0.14em] text-[#D86F55]">{message.sender_name}</p><p className="mt-2 whitespace-pre-wrap text-sm leading-6">{decrypted[message.id] || "Decrypting..."}</p>{message.was_censored && <p data-testid={`message-flag-${message.id}`} className="mt-2 flex items-center gap-1 text-xs text-[#D86F55]"><ShieldWarning size={14} /> Prohibited content removed before encryption</p>}</article>)}</div>
            <form data-testid="message-compose-form" onSubmit={sendMessage} className="mt-5 grid gap-3"><Textarea data-testid="message-compose-input" className="rounded-none border-[#8E3F31] bg-[#26352B] text-[#F7F2EA]" placeholder="Write an encrypted message" value={draft} onChange={(e) => setDraft(e.target.value)} /><Button data-testid="send-message-button" className="w-fit rounded-none bg-[#D86F55]"><PaperPlaneTilt size={18} /> Send encrypted message</Button></form>
          </>}
        </section>
      </section>
    </main>
  );
}